import { useEffect, useState, useRef } from 'react';
import { Box, Badge, IconButton, Typography, Divider, Button } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate } from 'react-router-dom';
import { getMyNotifications, markAsRead, markAllAsRead } from '../../api/notificationApi';
import { useNotifications } from '../../hooks/useNotifications';

const TYPE_ICONS = {
    SaleCreated: '\uD83D\uDED2', CommissionUpdated: '\uD83D\uDCB0', SaleEdited: '\u270F\uFE0F',
    UserCreated: '\uD83D\uDC64', CommissionCompleted: '\u2705',
    Mention: '\uD83D\uDCCC', NoticeCreated: '\uD83D\uDCE2',
};

export default function NotificationBell() {
    const navigate = useNavigate();
    const { unreadCount, pendingTaskCount, refreshCount } = useNotifications();
    const totalBadge = unreadCount + pendingTaskCount;
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const dropdownRef = useRef(null);

    const fetchNotifications = async () => {
        try {
            const res = await getMyNotifications(10);
            setNotifications(res.data);
        } catch (e) {
            console.error('Error fetching notifications:', e);
        }
    };

    const handleToggle = () => {
        if (!open) fetchNotifications();
        setOpen(!open);
    };

    const getNotifUrl = (n) => {
        if (n.redirectUrl) return n.redirectUrl;
        if (n.referenceType === 'Sales') return '/accounting/sales';
        if (n.referenceType === 'CommissionProgress') return '/accounting/salesCommissions';
        if (n.referenceType === 'User') return '/admin/users';
        return '/notifications';
    };

    const handleClickNotification = async (e, notification) => {
        if (!notification.isRead) {
            await markAsRead(notification.notificationId);
            refreshCount();
        }
        setOpen(false);
        const url = getNotifUrl(notification);
        if (e.button === 1 || e.ctrlKey || e.metaKey) {
            window.open(url, '_blank');
        } else {
            navigate(url);
        }
    };

    const handleMarkAllRead = async () => {
        await markAllAsRead();
        refreshCount();
        fetchNotifications();
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Box ref={dropdownRef} sx={{ position: 'relative' }}>
            <IconButton onClick={handleToggle} sx={{ color: '#64748B' }}>
                <Badge badgeContent={totalBadge} color="error" max={99}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}>
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            {open && (
                <Box sx={{
                    position: 'fixed', top: 56, right: 8, left: { xs: 8, md: 'auto' },
                    width: { xs: 'auto', md: 360 }, maxHeight: 440,
                    backgroundColor: '#fff', borderRadius: '14px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    zIndex: 1000, overflow: 'hidden', border: '1px solid #E2E8F0',
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>Notifications</Typography>
                        {unreadCount > 0 && (
                            <Button size="small" onClick={handleMarkAllRead}
                                sx={{ fontSize: '0.72rem', textTransform: 'none', fontFamily: "'Poppins', sans-serif", color: '#1A8CFF' }}>
                                Mark all read
                            </Button>
                        )}
                    </Box>
                    <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
                        {notifications.length === 0 ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.85rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>No notifications</Typography>
                            </Box>
                        ) : (
                            notifications.map(n => (
                                <Box key={n.notificationId}
                                    onMouseUp={(e) => handleClickNotification(e, n)}
                                    onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
                                    sx={{
                                        display: 'flex', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer',
                                        borderLeft: n.isRead ? 'none' : '3px solid #1A8CFF',
                                        backgroundColor: n.isRead ? 'transparent' : '#F0F9FF',
                                        '&:hover': { backgroundColor: '#F8FAFC' },
                                        borderBottom: '1px solid #F1F5F9',
                                    }}>
                                    <Box sx={{ fontSize: '1.1rem', mt: 0.25 }}>{TYPE_ICONS[n.type] || '\uD83D\uDD14'}</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: n.isRead ? 400 : 600, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>{n.title}</Typography>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.message}</Typography>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif", mt: 0.25 }}>{n.timeAgo}</Typography>
                                    </Box>
                                </Box>
                            ))
                        )}
                    </Box>
                    <Divider />
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                        <Button size="small" onClick={() => { setOpen(false); navigate('/notifications'); }}
                            sx={{ fontSize: '0.78rem', textTransform: 'none', fontFamily: "'Poppins', sans-serif", color: '#1A8CFF', fontWeight: 600 }}>
                            View All Notifications
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
